import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { TRexWordmark } from "./TRexWordmark";

/**
 * useCurrentYear — returns the current year in the visitor's local timezone
 * and schedules a re-render exactly at 00:00 on the next January 1st so
 * long-lived tabs (e.g. left open across New Year's Eve) roll over on
 * their own, without a manual refresh.
 */
function useCurrentYear() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  useEffect(() => {
    const now = new Date();
    const nextRollover = new Date(now.getFullYear() + 1, 0, 1, 0, 0, 0, 0);
    const delay = Math.max(0, nextRollover.getTime() - now.getTime());
    const t = setTimeout(() => setYear(new Date().getFullYear()), delay);
    return () => clearTimeout(t);
  }, [year]);
  return year;
}

const footerLinks = {
  network: [
    { label: "Overview", href: "/network" },
    { label: "Ledger", href: "/network/ledger" },
    { label: "Ecosystem", href: "/ecosystem" },
  ],
  solutions: [
    { label: "For Institutions", href: "/solutions/institutions" },
    { label: "For Builders", href: "/solutions/builders" },
    { label: "App Store", href: "/solutions/app-store" },
  ],
  foundation: [
    { label: "ERC-3643", href: "/network/erc-3643" },
    { label: "Articles", href: "/articles" },
    { label: "Docs", href: "https://docs.t-rex.network/", external: true },
    { label: "Governance", href: "/about#governance" },
  ],
  company: [
    { label: "About", href: "/about" },
    { label: "Press", href: "/press" },
    { label: "Contact", href: "/contact" },
  ],
};

const externalLinks = [
  { label: "ERC3643.org", href: "https://erc3643.org" },
];

const legalLinks = [
  { label: "Privacy", href: "/privacy" },
  { label: "Terms", href: "/terms" },
  { label: "Disclosures", href: "/disclosures" },
];

export function Footer() {
  const year = useCurrentYear();
  return (
    <footer className="border-t border-champ-light/10 bg-aubergine" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8 lg:py-16">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-flex items-center" data-testid="footer-logo" aria-label="T-REX Network">
              <TRexWordmark className="h-8 w-auto" />
            </Link>
            <p className="mt-4 text-sm text-bone/70 leading-relaxed max-w-sm">
              T-REX Network is the shared market infrastructure for tokenized securities, keeping ownership and compliance coherent across chains, venues and applications. Backed by Apex Group.
            </p>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 gap-8 sm:grid-cols-4">
            {Object.entries(footerLinks).map(([key, links]) => (
              <div key={key}>
                <h3 className="font-mono text-[10px] font-medium tracking-[0.08em] uppercase text-bone/40">{key}</h3>
                <ul className="mt-4 space-y-2.5">
                  {links.map((link) => (
                    <li key={link.label}>
                      {link.external ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-bone/70 transition-colors hover:text-bone"
                          data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                          {link.label}
                        </a>
                      ) : (
                        <Link to={link.href} className="text-sm text-bone/70 transition-colors hover:text-bone" data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* External destinations */}
        <div className="mt-12 pt-6 border-t border-champ-light/10 flex flex-wrap items-center gap-x-8 gap-y-2">
          {externalLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`footer-external-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-bone/40 hover:text-bone transition-colors"
            >
              {link.label}<ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>

        {/* Socials */}
        <div className="mt-6 flex items-center gap-4">
          <a href="https://github.com/ERC-3643" target="_blank" rel="noreferrer" data-testid="footer-social-github" className="text-bone/40 hover:text-bone transition-colors" aria-label="GitHub">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" /></svg>
          </a>
          <a href="https://x.com/trex_network" target="_blank" rel="noreferrer" data-testid="footer-social-x" className="text-bone/40 hover:text-bone transition-colors" aria-label="X (Twitter)">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
          </a>
          <a href="https://www.linkedin.com/company/t-rex-network/" target="_blank" rel="noreferrer" data-testid="footer-social-linkedin" className="text-bone/40 hover:text-bone transition-colors" aria-label="LinkedIn">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
          </a>
          <a href="https://discord.com/invite/3kf39pEBZn" target="_blank" rel="noreferrer" data-testid="footer-social-discord" className="text-bone/40 hover:text-bone transition-colors" aria-label="Discord">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" /></svg>
          </a>
          <a href="https://t.me/TREXNetwork" target="_blank" rel="noreferrer" data-testid="footer-social-telegram" className="text-bone/40 hover:text-bone transition-colors" aria-label="Telegram">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" /></svg>
          </a>
          <a href="https://www.youtube.com/@T-REXNetwork" target="_blank" rel="noreferrer" data-testid="footer-social-youtube" className="text-bone/40 hover:text-bone transition-colors" aria-label="YouTube">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
          </a>
        </div>

        {/* Legal row */}
        <div className="mt-8 pt-6 border-t border-champ-light/10 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="font-mono text-[10px] tracking-[0.04em] text-bone/40">
            &copy; {year} Digital Asset Operational Services ISAC Ltd.
          </p>
          <div className="flex items-center gap-5">
            {legalLinks.map((l) => (
              <Link key={l.label} to={l.href} className="font-mono text-[10px] uppercase tracking-[0.12em] text-bone/40 hover:text-bone transition-colors" data-testid={`footer-legal-${l.label.toLowerCase()}`}>
                {l.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => window.__trex_openCookieSettings && window.__trex_openCookieSettings()}
              className="font-mono text-[10px] uppercase tracking-[0.12em] text-bone/40 hover:text-bone transition-colors"
              data-testid="footer-cookies"
            >
              Cookies
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
